/* oxlint-disable import/no-unassigned-import -- tippy and component styles */
import { useRef, type ReactNode } from 'react';
import Tippy, { type TippyProps } from '@tippyjs/react';
import 'tippy.js/dist/border.css';
import 'tippy.js/dist/tippy.css';
import { useIntl } from 'react-intl';
import type { Plugin } from 'tippy.js';
import useWindowWidth from '../../utils/useWindowWidthHook';
import infoIcon from '../../assets/icons/info.svg';
import './InfoBubble.scss';
import s from './InfoBubble.module.scss';

/**
 * Tippy plugin hide on esc
 * https://atomiks.github.io/tippyjs/v6/plugins/
 */

const hideOnEsc: Plugin = {
  name: 'hideOnEsc',
  defaultValue: true,
  fn(instance) {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        instance.hide();
      }
    }

    return {
      onShow() {
        document.addEventListener('keydown', onKeyDown);
      },
      onHide() {
        document.removeEventListener('keydown', onKeyDown);
      },
    };
  },
};

const setDescribedBy: Plugin = {
  name: 'setDescribedBy',
  defaultValue: true,
  fn() {
    return {
      onShown(instance) {
        instance.reference.setAttribute('aria-describedby', instance.popper.id);
      },
      onHide(instance) {
        instance.reference.removeAttribute('aria-describedby');
      },
    };
  },
};

interface InfoBubbleProps {
  /** Content of the tooltip box e.g. the text you wish to display */
  children: ReactNode;
  /** Props to pass directly to Tippy. For a list of props see https://atomiks.github.io/tippyjs/ */
  tippyProps?: TippyProps;
  /**  Set a custom alt text for the info icon */
  iconAltText?: string;
}

/**
 * Renders an info icon with a tooltip. Children will be rendered in the tooltip.
 */
function InfoBubble({
  children,
  tippyProps,
  iconAltText = '',
}: InfoBubbleProps) {
  const intl = useIntl();
  const windowWidth = useWindowWidth();
  const referenceRef = useRef<HTMLDivElement>(null!);

  return (
    <>
      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div ref={referenceRef} className={s.imageWrapper} tabIndex={0}>
        <img
          src={infoIcon}
          alt={iconAltText || intl.formatMessage({ id: 'alt.iconInfo' })}
        />
      </div>
      <Tippy
        reference={referenceRef}
        content={children}
        theme="verbund"
        placement={windowWidth >= 768 ? 'right' : 'top'}
        trigger="mouseenter focus click"
        plugins={[hideOnEsc, setDescribedBy]}
        interactive
        // oxlint-disable-next-line react/jsx-props-no-spreading
        {...tippyProps}
      />
    </>
  );
}

export default InfoBubble;
